import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { CASE_STUDIES } from "@/data/content";

export default function CaseStudies() {
  useSEO(
    "Case Studies | Managed Global Teams in Action | Synergy Outsourcing",
    "See how managed global teams perform: operations support in Melbourne, outbound lead generation in Sydney and embedded professional services in Brisbane."
  );
  return (
    <>
      <PageHero
        eyebrow="Case studies"
        lines={["See what managed", "global teams can do."]}
        copy="Real engagement structures from Australian businesses working with Synergy Outsourcing teams across operations, lead generation and professional services."
      />
      <section className="py-14 sm:py-16" data-testid="case-studies-list">
        <div className="mx-auto max-w-5xl space-y-8 px-4 sm:px-6 lg:px-8">
          {CASE_STUDIES.map((cs, i) => (
            <Reveal key={cs.tag} delay={0.05}>
              <article data-testid={`case-study-detail-${i}`} className="rounded-2xl border border-white/10 bg-ink-2 p-8 sm:p-10">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-brand-soft px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-brand">
                    {cs.tag}
                  </span>
                  <span className="rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                    {cs.location}
                  </span>
                </div>
                <h2 className="mt-4 font-display text-2xl font-bold leading-snug text-white">{cs.title}</h2>
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  {cs.stats.map((s) => (
                    <div key={s.label} className="rounded-xl border border-brand/25 bg-brand-soft/50 p-5">
                      <p className="font-display text-3xl font-extrabold text-brand">{s.value}</p>
                      <p className="mt-1 text-sm text-mist">{s.label}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 grid gap-6 sm:grid-cols-3">
                  <div>
                    <h3 className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-brand">The challenge</h3>
                    <p className="mt-2 text-sm leading-relaxed text-mist">{cs.challenge}</p>
                  </div>
                  <div>
                    <h3 className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-brand">The approach</h3>
                    <p className="mt-2 text-sm leading-relaxed text-mist">{cs.approach}</p>
                  </div>
                  <div>
                    <h3 className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-brand">The delivery model</h3>
                    <p className="mt-2 text-sm leading-relaxed text-mist">{cs.model}</p>
                  </div>
                </div>
                <div className="mt-7 flex flex-wrap items-center gap-2 border-t border-white/10 pt-5">
                  <span className="text-xs text-slate-500">Roles involved:</span>
                  {cs.roles.map((r) => (
                    <span key={r} className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-slate-300">
                      {r}
                    </span>
                  ))}
                </div>
              </article>
            </Reveal>
          ))}
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-brand/30 bg-brand-soft/40 p-8 text-center sm:p-10">
              <h2 className="font-display text-xl font-bold text-white">Your business could be the next story here.</h2>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-mist">
                Start with a single role and grow from there. Most clients begin with one dedicated professional and scale as results come in.
              </p>
              <Link
                to="/get-your-team"
                data-testid="case-studies-cta"
                className="group mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-brand-bright"
              >
                Build Your Team
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      <CTASection />
    </>
  );
}
